import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import { ReportPeriod } from "../../utils/briefings/exportBriefingReport";

interface ReportPeriodDialogProps {
  open: boolean;
  onClose: () => void;
  onDownload: (period: ReportPeriod) => void;
  isLoading: boolean;
}

/**
 * Elige el periodo del report del briefing. Las dos fechas son opcionales: sin
 * inicio cuenta desde el principio de cada línea, sin fin hasta hoy.
 */
const ReportPeriodDialog: React.FC<ReportPeriodDialogProps> = ({
  open,
  onClose,
  onDownload,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const reversed =
    !!startDate && !!endDate && startDate.isAfter(endDate, "day");
  const hasPeriod = !!startDate || !!endDate;

  const download = () =>
    onDownload({
      startDate: startDate?.format("YYYY-MM-DD"),
      endDate: endDate?.format("YYYY-MM-DD"),
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t("briefingsView.reportPeriodTitle", "Report period")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "briefingsView.reportPeriodDesc",
              "Leave both dates empty to get the whole life of each line. Without a start date it counts from the start of each line; without an end date, up to today."
            )}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t("briefingsView.reportPeriodFrom", "From")}
              value={startDate}
              onChange={setStartDate}
              inputFormat="DD/MM/YYYY"
              renderInput={(params) => <TextField {...params} fullWidth />}
              disableMaskedInput
            />
            <DatePicker
              label={t("briefingsView.reportPeriodTo", "To")}
              value={endDate}
              onChange={setEndDate}
              inputFormat="DD/MM/YYYY"
              minDate={startDate ?? undefined}
              renderInput={(params) => <TextField {...params} fullWidth />}
              disableMaskedInput
            />
          </LocalizationProvider>
          {hasPeriod && (
            <Alert severity="info">
              {t(
                "briefingsView.reportPeriodBreakdownsNote",
                "Metrics and the daily and weekly series follow the period, and direct buys are prorated by days. Breakdowns (domains, creatives, devices, audiences) and reach are still for the whole campaign."
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={download}
          variant="contained"
          disabled={reversed || isLoading}
          startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
        >
          {t("briefingsView.downloadReport", "Download report")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportPeriodDialog;
